import { useCallback, useEffect, useRef, useState } from "react";
import {
  DUCK_SIDEBAR_DEFAULT_WIDTH,
  DUCK_SIDEBAR_MAX_WIDTH,
  DUCK_SIDEBAR_MIN_WIDTH,
} from "duck/ui/constants";

export interface UseDuckSidebarResize {
  onMouseDown: () => void;
  resizing: React.RefObject<boolean>;
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
}

export const useDuckSidebarResize = (): UseDuckSidebarResize => {
  const resizing = useRef(false);
  const [sidebarWidth, setSidebarWidth] = useState(DUCK_SIDEBAR_DEFAULT_WIDTH); // default width in px

  const onMouseDown = () => {
    resizing.current = true;
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  };

  const updateSidebarWidth = useCallback((width: number) => {
    setSidebarWidth(width);

    // We need to manually dispatch this event to trigger Vega's responsive behavior
    // which will then resize our charts inside our duck chat ..
    // - see https://vega.github.io/vega-lite/docs/size.html#specifying-responsive-width-and-height
    window.dispatchEvent(new Event("resize"));
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizing.current) return;

      const newWidth = window.innerWidth - e.clientX;
      if (
        newWidth > DUCK_SIDEBAR_MIN_WIDTH &&
        newWidth < DUCK_SIDEBAR_MAX_WIDTH
      ) {
        updateSidebarWidth(
          Math.max(
            DUCK_SIDEBAR_MIN_WIDTH,
            Math.min(DUCK_SIDEBAR_MAX_WIDTH, newWidth)
          )
        );
      }
    };
    const onMouseUp = () => {
      resizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [updateSidebarWidth]);

  return {
    onMouseDown,
    resizing,
    sidebarWidth,
    setSidebarWidth,
  };
};
